import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { supabase } from "@/integrations/supabase/client";
import { Form } from "@/components/ui/form";
import { toast } from "sonner";
import { AccountStep } from "./steps/AccountStep";
import { VerificationSuccess } from "./steps/VerificationSuccess";
import { signUpSchema, SignUpFormData } from "./types";
import { AuthError } from "@supabase/supabase-js";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "react-i18next";
import { showToast } from "@/lib/toast";

export const SignUpSteps = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const signUpForm = useForm<SignUpFormData>({
    resolver: zodResolver(signUpSchema),
    defaultValues: {
      email: "",
      password: "",
      confirmPassword: "",
      business_type: "company",
      country: "PT",
      business_name: "",
    },
  });

  const handleGoogleSignUp = async () => {
    try {
      setIsLoading(true);

      // Clear any existing sessions first
      await supabase.auth.signOut();

      // Make sure cookies are enabled and accessible
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/setup-account`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
          // Explicitly set a state parameter (optional, Supabase usually handles this)
          // state: generateRandomString(), // If you implement this function

          // Ensure cookies are properly handled
          skipBrowserRedirect: false,
        }
      });

      if (error) {
        console.error("[SignUp] Google sign up error:", error);
        showToast.error(t("toasts.error_signing_up_with_google"), {
          description: error.message,
        });
      }
    } catch (error) {
      console.error("[SignUp] Unexpected Google sign up error:", error);
      showToast.error(t("toasts.error_signing_up_with_google"), {
        description: "An unexpected error occurred. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = async (data: SignUpFormData) => {
    try {
      setIsLoading(true);

      await supabase.auth.signOut();

      const { data: signUpData, error } = await supabase.auth.signUp({
        email: data.email,
        password: data.password,
        options: {
          emailRedirectTo: `${window.location.origin}/setup-account`,
          data: {
            business_name: data.business_name,
            business_type: data.business_type,
            country: data.country,
            template: 'signup'
          }
        }
      });

      if (error) {
        console.error("[SignUp] Sign up error:", {
          error,
          message: error.message,
          name: error.name,
          stack: error.stack,
          status: error.status,
          details: error instanceof AuthError ? {
            status: error.status,
            name: error.name
          } : 'Not an AuthError'
        });

        if (error instanceof AuthError) {
          if (error.status === 422) {
            toast.error(t("toasts.account_exists"), {
              description: t("toasts.email_already_registered"),
              action: {
                label: t("hero.login"),
                onClick: () => navigate("/login")
              }
            });
            return;
          }

          if (error.message.includes("database")) {
            console.error("[SignUp] Database error details:", {
              message: error.message,
              status: error.status,
              name: error.name
            });
            toast.error(t("toasts.error_creating_account"), {
              description: t("toasts.issue_with_account_try_again"),
            });
            return;
          }

          toast.error(t("toasts.error_creating_account"), {
            description: error.message,
          });
        } else {
          toast.error(t("toasts.error_creating_account"), {
            description: t("toasts.unexpected_error_occured"),
          });
        }
        return;
      }

      setIsVerifying(true);
    } catch (error) {
      console.error("[SignUp] Unexpected error during sign up:", {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        name: error instanceof Error ? error.name : 'Unknown',
        stack: error instanceof Error ? error.stack : undefined
      });
      toast.error(t("toasts.error_creating_account"), {
        description: t("toasts.unexpected_error_occured"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return <VerificationSuccess
      title={t("auth.check_your_email")}
      message={t("auth.sent_confirmation_link")}
      submessage={t("auth.click_link_email")}
    />;
  }

  return (
    <div className="w-full max-width">
      <h2 className="text-4xl font-bold text-[#8791F3] pb-4">{t("auth.sign_up")}</h2>
      <Button
        type="button"
        variant="outline"
        className="w-full mb-4 flex items-center bg-white justify-center gap-2 hover:border-0 hover:text-[#34375B] hover:bg-[#5CC2BB] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
        onClick={handleGoogleSignUp}
        disabled={isLoading}
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path
            fill="currentColor"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="currentColor"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="currentColor"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="currentColor"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
        {t("hero.continue_with_google")}
      </Button>

      <div className="relative my-6">
        <div className="relative flex justify-center text-xs uppercase">
          <span className="px-2 text-muted-foreground">{t("hero.continue_with_email")}</span>
        </div>
      </div>

      <Form {...signUpForm}>
        <form onSubmit={signUpForm.handleSubmit(onSubmit)} className="space-y-4">
          <AccountStep form={signUpForm} />
          <button
            type="submit"
            className="w-full h-10 px-4 py-2 rounded-md text-[#F1F6F5] bg-[#8791F3] hover:text-[#34375B] hover:bg-[#5CC2BB] hover:shadow-[4px_4px_0px_0px_#34375B] hover:-translate-x-1 hover:-translate-y-1 transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? t("auth.signing_up") : t("auth.sign_up")}
          </button>
        </form>
      </Form>
    </div>
  );
};
